import { useEffect, useRef, useState } from "preact/hooks";

export type Remote<T> = { status: "loading" } | { status: "missing" } | { status: "ready"; data: T };

/**
 * The data a page shows: the prerendered `seed` when there is one, otherwise what `fetch` answers for `key`.
 * `fetch` answers undefined for something that does not exist (the page then shows "not found"); a failure
 * is the same, since a page that cannot be loaded has nothing more to show.
 */
export const useRemote = <T>(seed: T | undefined, key: string, fetch: () => Promise<T | undefined>): Remote<T> => {
  const [state, setState] = useState<Remote<T>>(seed === undefined ? { status: "loading" } : { status: "ready", data: seed });

  const seeded = useRef(seed !== undefined);

  useEffect(() => {
    // The first run of a seeded page has its data already; every later key is a page the seed was not made for.
    if (seeded.current) {
      seeded.current = false;
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetch()
      .then((data) => !cancelled && setState(data === undefined ? { status: "missing" } : { status: "ready", data }))
      .catch(() => !cancelled && setState({ status: "missing" }));
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
};
